import React, { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 48;

const makeParticles = (width, height) => (
  Array.from({ length: PARTICLE_COUNT }, (_, index) => ({
    x: Math.random() * width,
    y: Math.random() * height,
    radius: 1 + Math.random() * 2.8,
    speedX: (Math.random() - 0.5) * 0.18,
    speedY: (Math.random() - 0.5) * 0.16,
    alpha: 0.16 + Math.random() * 0.26,
    ringOffset: index / PARTICLE_COUNT
  }))
);

const DepthCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return undefined;
    }

    let particles = [];
    let animationFrame = null;
    let scrollDepth = 0;

    const setSize = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      particles = makeParticles(width, height);
    };

    const updateScrollDepth = () => {
      const maxScroll = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      scrollDepth = Math.min((window.scrollY || 0) / maxScroll, 1);
    };

    const drawRings = (width, height, time) => {
      const centerX = width * (0.72 - scrollDepth * 0.08);
      const centerY = height * (0.42 + scrollDepth * 0.06);
      const baseRadius = Math.min(width, height) * 0.16;

      for (let i = 0; i < 4; i += 1) {
        const radius = baseRadius + i * 42 + Math.sin(time * 0.0014 + i) * 8;
        context.beginPath();
        context.arc(centerX, centerY, radius, 0, Math.PI * 2);
        context.strokeStyle = `rgba(131, 225, 255, ${0.08 - i * 0.012})`;
        context.lineWidth = 1.2;
        context.stroke();
      }

      const gradient = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.9);
      gradient.addColorStop(0, 'rgba(131, 225, 255, 0.18)');
      gradient.addColorStop(0.4, 'rgba(131, 225, 255, 0.06)');
      gradient.addColorStop(1, 'rgba(131, 225, 255, 0)');
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(centerX, centerY, baseRadius * 1.9, 0, Math.PI * 2);
      context.fill();
    };

    const drawPaths = (width, height, time) => {
      const horizonY = height * (0.68 - scrollDepth * 0.1);

      context.lineWidth = 1.5;
      for (let i = 0; i < 5; i += 1) {
        context.beginPath();
        context.moveTo(width * (0.08 + i * 0.13), height + 40);
        context.bezierCurveTo(
          width * (0.18 + i * 0.1),
          height * (0.78 - scrollDepth * 0.08),
          width * (0.48 + i * 0.05),
          horizonY + Math.sin(time * 0.001 + i) * 14,
          width * 0.72,
          height * (0.46 + scrollDepth * 0.05)
        );
        context.strokeStyle = i % 2 === 0 ? 'rgba(131, 225, 255, 0.14)' : 'rgba(255, 211, 122, 0.09)';
        context.stroke();
      }
    };

    const drawParticles = (width, height, time) => {
      particles.forEach((particle) => {
        particle.x += particle.speedX + scrollDepth * 0.06;
        particle.y += particle.speedY - scrollDepth * 0.04;

        if (particle.x < -20) particle.x = width + 20;
        if (particle.x > width + 20) particle.x = -20;
        if (particle.y < -20) particle.y = height + 20;
        if (particle.y > height + 20) particle.y = -20;

        const pulse = 0.75 + Math.sin(time * 0.0016 + particle.ringOffset * Math.PI * 2) * 0.25;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.radius * pulse, 0, Math.PI * 2);
        context.fillStyle = `rgba(189, 232, 255, ${particle.alpha})`;
        context.fill();
      });
    };

    const render = (time) => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      context.clearRect(0, 0, width, height);
      drawPaths(width, height, time);
      drawRings(width, height, time);
      drawParticles(width, height, time);

      animationFrame = window.requestAnimationFrame(render);
    };

    setSize();
    updateScrollDepth();
    animationFrame = window.requestAnimationFrame(render);

    window.addEventListener('resize', setSize);
    window.addEventListener('scroll', updateScrollDepth, { passive: true });

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('resize', setSize);
      window.removeEventListener('scroll', updateScrollDepth);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: 0.78,
        zIndex: -1
      }}
    />
  );
};

export default DepthCanvas;
